import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LuChevronRight, LuSearch, LuUsers } from 'react-icons/lu';
import { useTheme } from '../../../contexts/ThemeContext';
import useSchoolStore from '../../../store/schoolStore';
import useStudentStore from '../../../store/studentStore';
import { getSectionsForClass, getClassName } from '../../../utils/schoolUtils';
import { cleanStudents, getStudentStatus } from '../../../utils/studentUtils';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import PageHeader from '../../../components/common/PageHeader';
import Table, { tdClass } from '../../../components/common/Table';
import Badge, { toneForStatus } from '../../../components/common/Badge';
import Avatar from '../../../components/common/Avatar';
import EmptyState from '../../../components/common/EmptyState';
import ErrorWidget from '../../../components/common/ErrorWidget';
import LoadingMessage from '../../../components/common/LoadingMessage';
import { Field, SearchInput, Select } from '../../../components/common/FormControls';

/**
 * Students of one class and section. Pick the class and section, press "Show students",
 * then filter the list by name. A row opens the student's details.
 * Data comes from useStudentStore.getStudentsOfClass.
 */
const StudentManagementPage = () => {
  const navigate = useNavigate();
  const { schoolId: routeSchoolId } = useParams();
  const { baseColors, isDark } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  const [filters, setFilters] = useState({
    Class: null,
    sec: null
  });

  const {
    schoolId,
    isClassesLoading,
    classes,
    getClasses
  } = useSchoolStore();

  const {
    students,
    isStudentsLoading,
    studentsError,
    getStudentsOfClass
  } = useStudentStore();

  useEffect(() => {
    if (!schoolId) {
      return;
    }
    // Don't wait for classes: the dropdown shows its own loading state
    getClasses();
  }, [schoolId, getClasses]);

  const handleSearch = () => {
    if (!schoolId) {
      return;
    }

    if (filters.Class && filters.sec) {
      getStudentsOfClass(schoolId, filters.Class, filters.sec);
    }
  };

  // Clean students first, then filter based on search term
  const cleanedStudents = cleanStudents(students);
  const filteredStudents = cleanedStudents.filter(student => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      student.name?.toLowerCase().includes(searchLower) ||
      student.studentId?.toLowerCase().includes(searchLower) ||
      student.email?.toLowerCase().includes(searchLower) ||
      student.phone?.toLowerCase().includes(searchLower)
    );
  });

  const renderResults = () => {
    if (isStudentsLoading) {
      return <LoadingMessage message="Loading students…" />;
    }

    if (studentsError) {
      return <ErrorWidget error={{ message: studentsError }} title="Could not load the students" onRetry={handleSearch} className="my-8 border-0 shadow-none" />;
    }

    if (!students || students.length === 0) {
      return (
        <EmptyState
          icon={<LuUsers />}
          title={!filters.Class || !filters.sec ? 'Choose a class and section' : 'No students in this section'}
          message={
            !filters.Class || !filters.sec
              ? 'Pick a class and a section above, then press “Show students”.'
              : 'No students were found for the selected class and section.'
          }
        />
      );
    }

    if (filteredStudents.length === 0) {
      return <EmptyState icon={<LuSearch />} title="No matching students" message={`No student’s name matches “${searchTerm}”.`} />;
    }

    return (
      <Table head={['Student', 'Class', 'Status', 'Parent', '']}>
        {filteredStudents.map((student, index) => {
          const status = getStudentStatus(student, baseColors, isDark);
          return (
            <tr
              key={student.id || index}
              onClick={() => navigate(`/dash/${routeSchoolId}/students/${student.userId || student.id}`)}
              className="cursor-pointer"
            >
              <td className={tdClass}>
                <div className="flex items-center gap-3">
                  <Avatar name={student.name} src={student.profileUrl} />
                  <div className="min-w-0">
                    <p className="truncate font-medium">{student.name || '—'}</p>
                    <p className="text-xs text-ui-ink-3">Adm. no. {student.admissionNo || '—'}</p>
                  </div>
                </div>
              </td>
              <td className={`${tdClass} whitespace-nowrap`}>
                {getClassName(schoolId, student.Class)} – {student.sec}
              </td>
              <td className={tdClass}>
                <Badge tone={toneForStatus(status.label)}>{status.label}</Badge>
              </td>
              <td className={tdClass}>
                <p>{student.motherName || student.fatherName || student.guardianName || '—'}</p>
                <p className="text-xs text-ui-ink-3">{student.parentPhone || student.fatherPhone || ''}</p>
              </td>
              <td className={`${tdClass} w-28 text-right`}>
                <Link
                  to={`/dash/${routeSchoolId}/students/${student.userId || student.id}`}
                  onClick={(event) => event.stopPropagation()}
                  className="inline-flex items-center gap-1 rounded text-[13px] font-medium text-ui-link hover:underline"
                >
                  View
                  <LuChevronRight aria-hidden className="size-4" />
                </Link>
              </td>
            </tr>
          );
        })}
      </Table>
    );
  };

  if (!schoolId) {
    return (
      <EmptyState title="No school selected" message="Open the dashboard from your school’s link to see its students." />
    );
  }

  const hasResults = !isStudentsLoading && !studentsError && cleanedStudents.length > 0;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Students" subtitle="Find a student by class and section, then open their details." />

      <Card className="mb-5">
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Field label="Class">
            <Select
              value={filters.Class || ''}
              onChange={(event) => {
                const selectedClass = event.target.value;
                setFilters(previousFilters => ({
                  ...previousFilters,
                  Class: selectedClass,
                  sec: null // Reset section when class changes
                }));
              }}
              disabled={isClassesLoading}
            >
              <option value="">{isClassesLoading ? 'Loading classes…' : 'Select class'}</option>
              {!isClassesLoading && classes && classes.map((classItem) => (
                <option key={classItem.Class} value={classItem.Class}>
                  {getClassName(schoolId, classItem.Class)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Section">
            <Select
              value={filters.sec || ''}
              onChange={(event) => setFilters(previousFilters => ({ ...previousFilters, sec: event.target.value }))}
              disabled={isClassesLoading || !filters.Class}
            >
              <option value="">{!filters.Class ? 'Select class first' : 'Select section'}</option>
              {!isClassesLoading && filters.Class && classes &&
                getSectionsForClass(classes, filters.Class).map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))
              }
            </Select>
          </Field>

          <Button
            onClick={handleSearch}
            disabled={!filters.Class || !filters.sec}
            loading={isStudentsLoading}
          >
            Show students
          </Button>
        </div>
      </Card>

      <Card
        noPadding
        title={hasResults ? `${filteredStudents.length} of ${cleanedStudents.length} students` : undefined}
        headerAction={
          hasResults && (
            <SearchInput
              placeholder="Filter by name"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full sm:w-64"
            />
          )
        }
      >
        {renderResults()}
      </Card>
    </div>
  );
};

export default StudentManagementPage;
